'use client';

import { useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DEFAULT_FEATURES, FEATURE_MONITORS, FEATURE_LOG_ANALYZER, FEATURE_TRIAGE, FEATURE_SCRAPER, getRouteForFeature } from '@/config/features';

/**
 * Features Hook
 * Provides feature access checks and utilities for feature-based access control
 * 
 * Usage:
 * const { hasFeature, availableFeatures, getFirstAvailableRoute } = useFeatures();
 * 
 * {hasFeature('scraper') && <ScraperTab />}
 */
export function useFeatures() {
  const { organization, hasFeature: contextHasFeature } = useAuth();

  /**
   * Get list of available feature IDs
   * Returns default features (monitors, triage) if no organization features exist
   */
  const availableFeatures = useMemo(() => {
    // If no organization, return default features
    if (!organization) {
      return [...DEFAULT_FEATURES];
    }
    
    // If no features array, return default features
    if (!organization.features || organization.features.length === 0) {
      return [...DEFAULT_FEATURES];
    }
    
    // Return only enabled features
    return organization.features
      .filter(feature => feature.enabled === true)
      .map(feature => feature.id);
  }, [organization]);

  /**
   * Check if a specific feature is available
   * Uses the context hasFeature method which handles default behavior
   */
  const hasFeature = useMemo(() => {
    return (featureId: string): boolean => {
      return contextHasFeature(featureId);
    };
  }, [contextHasFeature]);

  /**
   * Check if a feature is enabled (alias for hasFeature for clarity)
   */
  const isFeatureEnabled = useMemo(() => {
    return (featureId: string): boolean => {
      return contextHasFeature(featureId);
    };
  }, [contextHasFeature]);

  /**
   * Get the first available feature route
   * Useful for redirecting users when they don't have access to a requested feature
   */
  const getFirstAvailableRoute = useMemo(() => {
    return (): string => {
      // Priority order: monitors, triage, log-analyzer
      const priorityOrder = [FEATURE_MONITORS, FEATURE_TRIAGE, FEATURE_LOG_ANALYZER];
      
      for (const featureId of priorityOrder) {
        if (hasFeature(featureId)) {
          const route = getRouteForFeature(featureId);
          if (route) {
            return route;
          }
        }
      }
      
      // Fallback to monitors if nothing else is available
      return '/monitors';
    };
  }, [hasFeature]);

  /**
   * Check if user has access to monitors feature
   */
  const hasMonitors = useMemo(() => hasFeature(FEATURE_MONITORS), [hasFeature]);

  /**
   * Check if user has access to log analyzer feature
   */
  const hasLogAnalyzer = useMemo(() => hasFeature(FEATURE_LOG_ANALYZER), [hasFeature]);

  /**
   * Check if user has access to triage feature
   */
  const hasTriage = useMemo(() => hasFeature(FEATURE_TRIAGE), [hasFeature]);

  /**
   * Check if user has access to scraper feature
   */
  const hasScraper = useMemo(() => hasFeature(FEATURE_SCRAPER), [hasFeature]);

  return {
    hasFeature,
    isFeatureEnabled,
    availableFeatures,
    getFirstAvailableRoute,
    hasMonitors,
    hasLogAnalyzer,
    hasTriage,
    hasScraper,
    organization,
  };
}

